import React, { Fragment, useContext, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { components } from "react-select";
import { Flag, Zap, ArrowRight, Code2, Info, Maximize2, Minimize2 } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";
import ReactSelector from "../common/CommonReactSelector";
import HighlightedTextarea from "./HighlightedTextarea";

const Placeholder = (props) => <components.Placeholder {...props} />;

const SUBMISSION_RULE_PLACEHOLDER =
  'submission["field_id"] == null -> print("Heading cannot be empty")\n' +
  'submission["field_id"] != marker["Account No."] -> print("Account number mismatch")';

// Same step rail as the prototype (Status -> Active -> Move task ->
// Completion rule): each step lights up blue once reached, everything from
// the first not-yet-touched step onward stays gray -- purely a visual/UI
// concern driven by the `steps` list built in the component below.
const StepRail = ({ steps }) => {
  return (
    <div className="hidden sm:flex flex-col items-center pt-1 pr-5">
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          <div
            className={`flex items-center justify-center h-8 w-8 rounded-full border-2 shrink-0 transition-colors ${
              s.lit ? "bg-[#236DB4] border-[#236DB4] text-white" : "bg-white border-gray-200 text-gray-300"
            }`}
          >
            <s.Icon size={15} />
          </div>
          {i < steps.length - 1 && (
            <div className={`w-0.5 flex-1 min-h-7 my-0.5 ${s.lit ? "bg-[#9CBDDD]" : "bg-gray-200"}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Shared Add/Edit modal for a single Completion Rule.
 * Purely controlled -- CompletionRules.js supplies the values/handlers it
 * already uses today (create-form state for Add, a per-row draft for Edit),
 * this component owns no fetching/service calls of its own.
 */
const CompletionRuleModal = ({
  open,
  onClose,
  isNew,
  ruleNumber,
  statusOptions,
  statusValue,
  onStatusChange,
  isActive,
  onToggleActive,
  moveTask,
  onToggleMoveTask,
  workflowOptions,
  targetWorkflowValue,
  onTargetWorkflowChange,
  criteria,
  onCriteriaChange,
  onSave,
  saveDisabled,
}) => {
  const { langMode } = useContext(AppContext);
  const isRtl = langMode === "ar" || langMode === "ur";

  // Tracks which steps the user has interacted with in this modal session,
  // purely to drive the step-rail's progressive lighting (prototype
  // behavior) -- doesn't affect any save/validation logic below. Reset
  // whenever the dialog opens so a fresh "Add new rule" always starts from
  // step 1, same as the edit modal always starting fully lit.
  const [touched, setTouched] = useState(() => (isNew ? { active: false, moveTask: false } : { active: true, moveTask: true }));
  // Prototype's expression editor can be expanded to a fullscreen overlay;
  // purely a UI affordance over the same `criteria` value/`onCriteriaChange`
  // handler already used inline.
  const [criteriaFullscreen, setCriteriaFullscreen] = useState(false);
  useEffect(() => {
    if (open) {
      setTouched(isNew ? { active: false, moveTask: false } : { active: true, moveTask: true });
      setCriteriaFullscreen(false);
    }
  }, [open, isNew]);
  const markTouched = (key) => setTouched((t) => (t[key] ? t : { ...t, [key]: true }));

  // Task movement (Move Task/destination workflow) only applies to NOT_COMPLETED rules, so the step is
  // hidden for every other status (e.g. SUBMISSION) and the completion-rule step moves up to step 3.
  // CompletionRules.js also drops moveTask/targetWorkflowId from the save/update payload for those statuses.
  const showMoveTaskStep = (statusValue?.value ?? statusValue) === "NOT_COMPLETED";
  const rawSteps = [
    { key: "status", Icon: Flag, done: true },
    { key: "active", Icon: Zap, done: touched.active },
    ...(showMoveTaskStep
      ? [{ key: "moveTask", Icon: ArrowRight, done: touched.moveTask && (!moveTask || !!targetWorkflowValue) }]
      : []),
    { key: "criteria", Icon: Code2, done: !!(criteria && criteria.trim().length > 0) },
  ];
  let reachedGap = false;
  const steps = rawSteps.map((s) => {
    if (reachedGap || !s.done) reachedGap = true;
    return { ...s, lit: !reachedGap };
  });

  // The SUBMISSION status gets its own placeholder: one example for a required field and one for
  // an account-number mismatch. Other statuses keep the generic example.
  const isSubmissionStatus = (statusValue?.value ?? statusValue) === "SUBMISSION";

  const criteriaEditor = (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
          <Code2 size={14} className="text-[#236DB4]" /> {translations[langMode].completionRuleColumn} &middot; {translations[langMode].expressionEditor}
        </div>
        <button
          type="button"
          onClick={() => setCriteriaFullscreen((f) => !f)}
          className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800 transition-colors"
        >
          {criteriaFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          {criteriaFullscreen ? translations[langMode].exitFullscreen : translations[langMode].fullscreen}
        </button>
      </div>
      {isSubmissionStatus && (
        <div className="m-3 mb-0 flex items-start gap-3 rounded-xl border border-[#d5e5f7] bg-[#eef5fd] px-4 py-3 text-sm text-[#236DB4]">
          <Info size={18} className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">
              {translations[langMode]?.completionSubmissionNoteTitle ?? "Define at least one rule for a task to be completed successfully."}
            </p>
            <ol className="mt-1 list-decimal space-y-0.5 ps-4 text-xs leading-5 text-[#3a73ad]">
              <li>{translations[langMode]?.completionSubmissionRuleRequired ?? "Required field: shows the message when the field is left empty."}</li>
              <li>{translations[langMode]?.completionSubmissionRuleMismatch ?? "Number mismatch: shows the message when the submitted number does not match the account number."}</li>
            </ol>
          </div>
        </div>
      )}
      <div className={criteriaFullscreen ? "p-3" : "p-3 max-h-72 overflow-y-auto"}>
        <HighlightedTextarea
          value={criteria}
          onChange={onCriteriaChange}
          rows={criteriaFullscreen ? 16 : 5}
          placeholder={isSubmissionStatus ? SUBMISSION_RULE_PLACEHOLDER : translations[langMode].criteriaPlaceholder}
          showLineNumbers
        />
      </div>
    </div>
  );

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>
        <div className="fixed inset-0 z-0 flex items-center justify-center p-2 sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden"
              style={{ maxHeight: "90vh" }}
            >
              {/* Scroll lives on this inner wrapper, not the rounded Dialog.Panel itself --
                  overflow-y-auto and rounded-2xl on the same element makes Chromium render
                  the scrollbar track squared-off past the rounded corner, showing up as a
                  stray dark seam against the backdrop. The outer panel just clips (overflow-hidden). */}
              <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
                <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                  <Dialog.Title className="text-base font-semibold text-gray-800 truncate">
                    {isNew
                      ? translations[langMode].addNewRule
                      : `${translations[langMode].editRule}${Number.isInteger(ruleNumber) ? ` #${ruleNumber}` : ""}`}
                  </Dialog.Title>
                  <ModalCloseButton onClick={onClose} />
                </div>

              <div className="px-4 sm:px-6 py-5">
                <div className="flex">
                  <StepRail steps={steps} />
                  <div className="flex-1 space-y-6 min-w-0">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold tracking-wide text-[#236DB4]">
                          {translations[langMode].step} 1
                        </span>
                        <span className="text-sm font-semibold text-gray-800">
                          {translations[langMode].status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        {translations[langMode].statusStepDescription}
                      </p>
                      <ReactSelector
                        options={statusOptions}
                        components={Placeholder}
                        placeholder={translations[langMode].selectStatus}
                        onChange={onStatusChange}
                        height="42px"
                        menuPortalTarget={document.body}
                        value={statusValue}
                      />
                    </div>

                    <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                      <div className="text-xs font-bold tracking-wide text-[#236DB4] mb-2">
                        {translations[langMode].step} 2
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-800">
                              {translations[langMode].ruleActive}
                            </span>
                            <span
                              className={`text-xs font-semibold px-1.5 py-0.5 rounded ${isActive ? "" : "bg-gray-100 text-gray-500"}`}
                              style={isActive ? { color: "#188A4B", background: "#E7F6ED" } : undefined}
                            >
                              {isActive ? "ON" : "OFF"}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5 leading-snug">
                            {translations[langMode].ruleActiveDescription}
                          </p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={isActive}
                          onClick={() => {
                            onToggleActive(!isActive);
                            markTouched("active");
                          }}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#236DB4] ${isActive ? "" : "bg-gray-200"}`}
                          style={isActive ? { background: "#188A4B" } : undefined}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                              isActive ? (isRtl ? "-translate-x-6" : "translate-x-6") : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {showMoveTaskStep && (
                    <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 space-y-3">
                      <div className="text-xs font-bold tracking-wide text-[#236DB4]">
                        {translations[langMode].step} 3
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-800">
                              {translations[langMode].taskMovement}
                            </span>
                            <span
                              className={`text-xs font-semibold px-1.5 py-0.5 rounded ${moveTask ? "" : "bg-gray-100 text-gray-500"}`}
                              style={moveTask ? { color: "#188A4B", background: "#E7F6ED" } : undefined}
                            >
                              {moveTask ? "ON" : "OFF"}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5 leading-snug">
                            {translations[langMode].taskMovementDescription}
                          </p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={moveTask}
                          onClick={() => {
                            onToggleMoveTask(!moveTask);
                            markTouched("moveTask");
                          }}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#236DB4] ${moveTask ? "" : "bg-gray-200"}`}
                          style={moveTask ? { background: "#188A4B" } : undefined}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                              moveTask ? (isRtl ? "-translate-x-6" : "translate-x-6") : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                      {moveTask && (
                        <div className="pl-1 pt-1 border-l-2 border-green-200 ml-1">
                          <div className="pl-4">
                            <label className="text-xs font-semibold text-gray-500 mb-1.5 block">
                              {translations[langMode].destinationTask}
                            </label>
                            <ReactSelector
                              options={workflowOptions}
                              components={Placeholder}
                              placeholder={translations[langMode].noactivity}
                              onChange={(v) => {
                                onTargetWorkflowChange(v);
                                markTouched("moveTask");
                              }}
                              isClearable={false}
                              height="42px"
                              menuPortalTarget={document.body}
                              value={targetWorkflowValue}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    )}

                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold tracking-wide text-[#236DB4]">
                          {translations[langMode].step} {showMoveTaskStep ? 4 : 3}
                        </span>
                        <span className="text-sm font-semibold text-gray-800">
                          {translations[langMode].completionRuleColumn}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        {translations[langMode].completionRuleDescPrefix}{" "}
                        <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">TRUE</span>{" "}
                        {translations[langMode].completionRuleDescSuffix}
                      </p>
                      {!criteriaFullscreen && criteriaEditor}
                    </div>
                  </div>
                </div>
              </div>

                <div className="flex items-center justify-end gap-2 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 sticky bottom-0 bg-white w-full">
                  <CommonButton colorIntent="danger"
                    onClick={onClose}
                    variant="danger"
                    wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                    className="w-full !h-10 !rounded-lg justify-center"
                  >
                    {translations[langMode].cancle}
                  </CommonButton>
                  <CommonButton colorIntent="success"
                    onClick={onSave}
                    variant="primary"
                    disabled={saveDisabled}
                    wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                    className="w-full !h-10 !rounded-lg justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isNew ? translations[langMode].save : translations[langMode].update}
                  </CommonButton>
                </div>
              </div>

              {criteriaFullscreen && (
                <div
                  className="fixed inset-0 z-[60] bg-gray-900/60 flex items-center justify-center p-8"
                  onClick={() => setCriteriaFullscreen(false)}
                >
                  <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
                    {criteriaEditor}
                  </div>
                </div>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CompletionRuleModal;
